import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, X, ShieldStar, ShieldCheck } from "@phosphor-icons/react";
import { Heading } from "./NKDaily";

export default function SubAdmins() {
  const [rows, setRows] = useState([]);
  const [perms, setPerms] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [admins, available] = await Promise.all([
        api.get("/admin/sub-admins"),
        api.get("/admin/permissions/available"),
      ]);
      setRows(admins.data);
      setPerms(available.data.permissions);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed (super-admin only)");
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (payload) => {
    try {
      await api.post("/admin/sub-admins", payload);
      toast.success("Sub-admin created");
      setCreating(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const update = async (user_id, payload) => {
    try {
      await api.put(`/admin/sub-admins/${user_id}`, payload);
      toast.success("Updated");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (u) => {
    if (!confirm(`Revoke admin access for ${u.email}? They'll be demoted to a regular player.`)) return;
    try {
      await api.delete(`/admin/sub-admins/${u.user_id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <Heading title="Administrators"
          subtitle="Super-admin manages full access. Sub-admins get scoped permissions you pick below." />
        <button onClick={() => setCreating(true)} data-testid="create-sub-admin-btn"
          className="px-4 py-2 bg-nk-accent text-nk-bg text-sm font-medium rounded-sm inline-flex items-center gap-2 shrink-0">
          <Plus size={14} weight="bold" /> New sub-admin
        </button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((u) => (
          <div key={u.user_id} className="nk-card p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  {u.role === "super_admin"
                    ? <ShieldStar size={14} weight="duotone" className="text-nk-accent" />
                    : <ShieldCheck size={14} weight="duotone" className="text-nk-mute" />}
                  <p className="font-display text-base">{u.name || u.email.split("@")[0]}</p>
                </div>
                <p className="text-xs text-nk-mute font-mono">{u.email}</p>
              </div>
              {u.role !== "super_admin" && (
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => setEditing(u)} className="text-xs px-2 py-1 text-nk-mute hover:text-nk-text border border-nk-line rounded-sm">Edit</button>
                  <button onClick={() => remove(u)} className="text-nk-mute hover:text-nk-bad p-1"><Trash size={14} /></button>
                </div>
              )}
            </div>
            <div>
              <p className="text-[10px] text-nk-dim uppercase tracking-wider mb-1">
                {u.role === "super_admin" ? "Access" : "Permissions"}
              </p>
              {u.role === "super_admin" ? (
                <p className="text-xs text-nk-accent">Full access · all features</p>
              ) : (u.permissions?.length || 0) === 0 ? (
                <p className="text-xs text-nk-dim">No permissions assigned</p>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {u.permissions.map((p) => (
                    <span key={p} className="text-[10px] px-1.5 py-0.5 border border-nk-line rounded-sm uppercase tracking-wider">{p}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {creating && <SubAdminForm perms={perms} onCancel={() => setCreating(false)} onSave={create} />}
      {editing && <SubAdminForm initial={editing} perms={perms}
                                onCancel={() => setEditing(null)}
                                onSave={(p) => update(editing.user_id, p)} />}
    </div>
  );
}

function SubAdminForm({ initial, perms, onCancel, onSave }) {
  const isEdit = !!initial;
  const [email, setEmail] = useState(initial?.email || "");
  const [name, setName] = useState(initial?.name || "");
  const [password, setPassword] = useState("");
  const [selected, setSelected] = useState(initial?.permissions || []);

  const toggle = (perm) => {
    setSelected((s) => s.includes(perm) ? s.filter((p) => p !== perm) : [...s, perm]);
  };

  const submit = () => {
    if (!isEdit && (!email || !password || !name)) {
      toast.error("Fill all fields"); return;
    }
    if (password && password.length < 8) {
      toast.error("Password must be 8+ chars"); return;
    }
    const payload = isEdit
      ? { permissions: selected, name: name || undefined, password: password || undefined }
      : { email, name, password, permissions: selected };
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onCancel}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg bg-nk-bg border-l border-nk-line p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-medium">{isEdit ? "Edit sub-admin" : "New sub-admin"}</h2>
          <button onClick={onCancel} className="text-nk-mute hover:text-nk-text"><X size={18} /></button>
        </div>
        <div className="space-y-3 text-sm">
          {!isEdit && (
            <input type="email" placeholder="email@example.com" value={email}
                   onChange={(e) => setEmail(e.target.value)}
                   data-testid="sub-admin-email"
                   className="w-full bg-nk-surface border border-nk-line px-3 py-2 rounded-sm focus:border-nk-accent outline-none" />
          )}
          <input placeholder="Display name" value={name}
                 onChange={(e) => setName(e.target.value)}
                 data-testid="sub-admin-name"
                 className="w-full bg-nk-surface border border-nk-line px-3 py-2 rounded-sm focus:border-nk-accent outline-none" />
          <input type="password" placeholder={isEdit ? "Leave blank to keep current password" : "Password (8+ chars, letters + numbers)"}
                 value={password}
                 onChange={(e) => setPassword(e.target.value)}
                 data-testid="sub-admin-password"
                 className="w-full bg-nk-surface border border-nk-line px-3 py-2 rounded-sm focus:border-nk-accent outline-none" />

          <div className="pt-2">
            <p className="text-[11px] text-nk-mute uppercase tracking-wider mb-2">Permissions</p>
            <div className="grid grid-cols-2 gap-1.5">
              {perms.map((perm) => (
                <label key={perm} className="flex items-center gap-2 text-xs px-2 py-1.5 border border-nk-line rounded-sm cursor-pointer hover:border-nk-accent">
                  <input type="checkbox" checked={selected.includes(perm)}
                         onChange={() => toggle(perm)} className="accent-nk-accent" />
                  <span className="capitalize">{perm.replace("_", " ")}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-3">
            <button onClick={submit} data-testid="sub-admin-save-btn"
                    className="flex-1 px-4 py-2 bg-nk-accent text-nk-bg text-sm font-medium rounded-sm hover:bg-nk-accentSoft">
              {isEdit ? "Save" : "Create sub-admin"}
            </button>
            <button onClick={onCancel} className="px-4 py-2 border border-nk-line text-sm text-nk-mute rounded-sm">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
